import { FC, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import { styled } from '@mui/material/styles'
import { GetRGBA, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

type BannerVariant = 'info' | 'success' | 'warning' | 'error'

interface GenericBannerProps {
  message: string
  onClose: () => void
  autoCloseDuration?: number
  icon?: React.ReactNode
  variant?: BannerVariant
}

const variantIcons: Record<BannerVariant, React.ReactNode> = {
  info: <InfoIcon fontSize="small" />,
  success: <CheckCircleIcon fontSize="small" />,
  warning: <WarningIcon fontSize="small" />,
  error: <ErrorIcon fontSize="small" />,
}

const variantIconColorNames: Record<BannerVariant, string> = {
  info: MUIThemeColors.infoMain,
  success: MUIThemeColors.successMain,
  warning: MUIThemeColors.warningMain,
  error: MUIThemeColors.errorMain,
}

export const GenericBanner: FC<GenericBannerProps> = ({
  message,
  onClose,
  autoCloseDuration = 5000,
  icon,
  variant = 'info',
}) => {
  useEffect(() => {
    if (autoCloseDuration <= 0) return
    const timer = setTimeout(onClose, autoCloseDuration)
    return () => clearTimeout(timer)
  }, [onClose, autoCloseDuration])

  return createPortal(
    <BannerWrapper role="status" aria-live="polite" variant={variant}>
      <BannerIcon variant={variant}>{icon ?? variantIcons[variant]}</BannerIcon>
      <BannerMessage>{message}</BannerMessage>
      <BannerCloseButton onClick={onClose} aria-label="Close banner" size="small">
        <CloseIcon fontSize="small" />
      </BannerCloseButton>
    </BannerWrapper>,
    document.body,
  )
}

const BannerWrapper = styled(Box)<{ variant: BannerVariant }>(({ theme }) => ({
  position: 'fixed',
  top: '5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 99999,
  width: '90vw',
  maxWidth: '700px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2.5),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  color: getThemeColor(theme, MUIThemeColors.black),
  border: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.1)}`,
}))

const BannerIcon = styled(Box)<{ variant: BannerVariant }>(({ theme, variant }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  color: getThemeColor(theme, variantIconColorNames[variant] as MUIThemeColors),
}))

const BannerMessage = styled(Typography)(({ theme }) => ({
  flex: 1,
  fontSize: '14px',
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.black),
}))

const BannerCloseButton = styled(IconButton)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.black),
  padding: 0,
  flexShrink: 0,
  '&:hover': {
    background: getThemeColor(theme, MUIThemeColors.secondaryLight),
  },
}))
